import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import AppSplash from '../components/common/AppSplash';

const ACTIVE_ROLE_KEY = 'tippi_activeRole';
const VIEWING_AS_KEY = 'tippi_viewingAs';
const VIEWABLE_ROLES = ['driver', 'orderer', 'admin'];

// Intro-animasjonen (TruckIntro) er ferdig tegnet etter ca. 1,24 s: siste kroppsstrek
// har delay 6 * 0,09 s + 0,7 s varighet (hjulene 1,2 s, stripene 1,05 s, lasten 0,6 s).
// Splashen holdes minst så lenge, pluss litt margin, selv om auth er ferdig lastet før det.
export const SPLASH_MIN_MS = 1700;

// Gjelder kun første splash per sideinnlasting; senere auth-laster viser ikke splash på nytt.
let introSplashDone = false;
export function resetIntroSplashForTests() { introSplashDone = false; }

function prefersReducedMotion() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const AuthContext = createContext(null);

function resolveActiveRole(userRoles, stored) {
  if (stored && userRoles.includes(stored)) return stored;
  return userRoles[0] ?? null;
}

export function AuthProvider({ children }) {
  const [currentUser,    setCurrentUser]    = useState(null);
  const [userDoc,        setUserDoc]        = useState(null);
  const [userRoles,      setUserRoles]      = useState([]);
  const [activeRole,     setActiveRoleState] = useState(null);
  const [viewingAs,      setViewingAsState] = useState(null);
  const [loading,        setLoading]        = useState(true);
  // Statisk lastebil ved redusert bevegelse: ingen minimumstid.
  const [holdSplash,     setHoldSplash]     = useState(() => {
    if (introSplashDone) return false;
    if (prefersReducedMotion()) { introSplashDone = true; return false; }
    return true;
  });

  useEffect(() => {
    if (!holdSplash) return undefined;
    const id = setTimeout(() => {
      introSplashDone = true;
      setHoldSplash(false);
    }, SPLASH_MIN_MS);
    return () => clearTimeout(id);
  }, [holdSplash]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (snap.exists()) {
            const data = snap.data();
            // Compatibility fallback: support old schema during migration window
            const roles = data.roles ?? (data.role ? [data.role] : []);
            setUserDoc(data);
            setUserRoles(roles);
            const stored = localStorage.getItem(ACTIVE_ROLE_KEY);
            setActiveRoleState(resolveActiveRole(roles, stored));
            // Admin som sist så appen som en annen rolle, åpner samme visning igjen.
            const storedView = localStorage.getItem(VIEWING_AS_KEY);
            if (roles.includes('admin') && VIEWABLE_ROLES.includes(storedView)) setViewingAsState(storedView);
          }
        } catch (e) {
          console.error('AuthContext: kunne ikke laste brukerdokument', e);
        }
        setCurrentUser(firebaseUser);
      } else {
        setCurrentUser(null);
        setUserDoc(null);
        setUserRoles([]);
        setActiveRoleState(null);
        setViewingAsState(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  function setActiveRole(role) {
    if (!userRoles.includes(role)) return;
    setActiveRoleState(role);
    localStorage.setItem(ACTIVE_ROLE_KEY, role);
  }

  function setViewingAs(role) {
    setViewingAsState(role);
    if (role) localStorage.setItem(VIEWING_AS_KEY, role);
    else localStorage.removeItem(VIEWING_AS_KEY);
  }

  async function refreshUserDoc() {
    if (!currentUser) return;
    const snap = await getDoc(doc(db, 'users', currentUser.uid));
    if (snap.exists()) {
      const data = snap.data();
      const roles = data.roles ?? (data.role ? [data.role] : []);
      setUserDoc(data);
      setUserRoles(roles);
      setActiveRoleState(prev => resolveActiveRole(roles, prev));
    }
  }

  const effectiveRole = viewingAs ?? activeRole;

  const value = {
    currentUser,
    userDoc,
    userRoles,
    activeRole,
    setActiveRole,
    viewingAs,
    setViewingAs,
    effectiveRole,
    loading,
    refreshUserDoc,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading || holdSplash ? <AppSplash /> : children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth må brukes innenfor AuthProvider');
  return context;
}
